import { useContext } from "react";
import { AppContext } from "../App";
import PageHero from "./PageHero";
import CallToAction from "./callToAction";
import civilImage from "../assets/civil engineering (1).jpg";
import buildingImage from "../assets/building construction (1).jpg";
import designImage from "../assets/construction design (1).jpg";
import constructionImage from "../assets/construction1.png";
import siteImage from "../assets/construction2.png";

export const serviceCategories = [
  {
    slug: "civil-engineering-works",
    title: "Civil Engineering Works",
    navTitle: "Civil Works",
    heroImage: civilImage,
    summary:
      "Roads, infrastructure, and general civil construction delivered with practical planning and disciplined site execution.",
    intro:
      "SMART TGM Limited supports civil works for clients who need dependable execution, clear project control, and quality outcomes across public, private, and institutional environments.",
    subServices: ["Roads", "Infrastructure", "General civil construction"],
    highlights: [
      "Site coordination from mobilization to commissioning",
      "Civil works delivered with quality and cost awareness",
      "Structured follow-up with project stakeholders",
    ],
  },
  {
    slug: "building-construction",
    title: "Building Construction",
    navTitle: "Buildings",
    heroImage: buildingImage,
    summary:
      "Residential and commercial building construction shaped around workmanship, coordination, and client objectives.",
    intro:
      "From small to medium-size projects, SMART TGM Limited manages building works with reliable supervision, clear schedules, and close attention to finish quality.",
    subServices: ["Residential buildings", "Commercial buildings"],
    highlights: [
      "Residential and commercial delivery capability",
      "Specialist trade coordination for smoother progress",
      "Quality supervision for durable finished work",
    ],
  },
  {
    slug: "project-management",
    title: "Project Management",
    navTitle: "Management",
    heroImage: constructionImage,
    summary:
      "Coordination, planning, execution, and progress tracking for construction projects with many moving parts.",
    intro:
      "Our project management approach emphasizes detailed schedules, resource planning, clear communication, and consistent progress tracking so client objectives remain the priority.",
    subServices: ["Coordinating specialist trades", "Planning and execution"],
    highlights: [
      "Detailed schedules and resource plans",
      "Clear communication with project stakeholders",
      "Progress tracking and timely adjustments",
    ],
  },
  {
    slug: "engineering-design-solutions",
    title: "Engineering & Design Solutions",
    navTitle: "Design",
    heroImage: designImage,
    summary:
      "Design inputs and technical solutions provided as value-added support during construction planning and delivery.",
    intro:
      "SMART TGM Limited provides practical engineering input to help clients improve buildability, resolve site challenges, and make confident technical decisions.",
    subServices: ["Design inputs", "Technical solutions"],
    highlights: [
      "Value-added engineering support",
      "Buildability-focused design input",
      "Technical coordination with project teams",
    ],
  },
  {
    slug: "specialized-construction-works",
    title: "Specialized Construction Works",
    navTitle: "Specialized",
    heroImage: siteImage,
    summary:
      "Focused construction works including fencing, roofing, cladding, and partitioning, grounded in previous project experience.",
    intro:
      "Our project experience includes specialized scopes that require clean workmanship, efficient coordination, and reliable completion for both private and institutional clients.",
    subServices: ["Fencing (3D mesh)", "Roofing", "Cladding", "Partitioning"],
    highlights: [
      "3D mesh fencing and boundary works",
      "Roofing and finishing support",
      "Cladding and partitioning execution",
    ],
  },
];

export const serviceMenuGroups = serviceCategories.map((service) => ({
  title: service.title,
  menuTitle: service.title,
  slug: service.slug,
  items: service.subServices,
}));

export function getServiceBySlug(slug) {
  return serviceCategories.find((service) => service.slug === slug);
}

export default function ServicesContent() {
  const appContext = useContext(AppContext);

  return (
    <>
      <PageHero
        eyebrow="Services"
        title="Construction services shaped around assured project execution."
        text="SMART TGM Limited brings civil works, building construction, project management, engineering input, and specialized construction scopes into one coordinated delivery partner."
        image={civilImage}
      />

      <section className="tgm-section tgm-services-page" data-aos="fade-up">
        <div className="container">
          <div className="tgm-section-head">
            <div>
              <p className="tgm-section-kicker">What We Do</p>
              <h2>Built for practical construction needs.</h2>
            </div>
            <p>
              Each service category is structured to help clients move from
              planning to completion with clear communication and dependable
              site follow-through.
            </p>
          </div>

          <div className="tgm-service-page-grid">
            {serviceCategories.map((service, index) => (
              <article
                className="tgm-service-page-card"
                key={service.slug}
                data-aos="fade-up"
                data-aos-delay={index * 60}
              >
                <div className="tgm-service-page-media">
                  <img src={service.heroImage} alt={service.title} />
                </div>
                <div className="tgm-service-page-body">
                  <span>{service.navTitle}</span>
                  <h3>{service.title}</h3>
                  <p>{service.summary}</p>
                  <ul>
                    {service.subServices.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="tgm-text-link"
                    onClick={() =>
                      appContext.navTo({
                        item: "services",
                        subItem: service.slug,
                      })
                    }
                  >
                    Explore service
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <CallToAction />
    </>
  );
}
